import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { useBasePath, focusHref } from '../../../../lib/config/nav-helpers';
import { DataCard, StatusBadge, SkeletonCard, QueryError } from '../../../../components/ui';
import { NotConnected } from '../../../../components/ui/NotConnected';
import { sees } from '../../../../lib/coverage';

export interface WeeklyPulseWidgetProps {
  digest: { data?: any; isLoading: boolean; isError: boolean; error: unknown; refetch: () => void };
}

export function WeeklyPulseWidget({ digest }: WeeklyPulseWidgetProps) {
  const navigate = useNavigate();
  const basePath = useBasePath();

  // The API keeps only the deltas whose source is connected and synced; with none left, name what is missing.
  const deltas: any[] = digest.data?.metricDeltas ?? [];
  const gap = (['money', 'crm', 'mailbox'] as const).find((n) => !sees(digest.data?.coverage, n));

  return (
    <DataCard title="Weekly Pulse" badge={digest.data ? <StatusBadge status="info" label={digest.data.weekLabel} size="xs" /> : undefined}>
      {digest.isLoading ? <SkeletonCard /> : digest.isError ? <QueryError error={digest.error} onRetry={() => digest.refetch()} compact /> : digest.data ? (
        <div className="space-y-3">
          {deltas.length === 0 && gap && <NotConnected need={gap} />}
          {deltas.slice(0, 4).map((d: any) => {
            const target = d.metricId === 'pipe' || d.metricId === 'deals' || d.metricId === 'cycle'
              ? '/pipeline'
              : '/revenue';
            return (
              <button type="button" key={d.label} onClick={() => navigate(focusHref(basePath, target, d.metricId))} className="w-full text-left flex items-center justify-between rounded-md -mx-2 px-2 py-1 cursor-pointer hover:bg-surface-1 transition-colors">
                <span className="text-2xs text-ink-muted">{d.label}</span>
                <span className={clsx('text-2xs font-mono font-semibold tabular-nums', d.direction === 'flat' ? 'text-ink-dim' : d.isBetter ? 'text-success' : 'text-danger')}>
                  {d.deltaPct >= 0 ? '+' : ''}{d.deltaPct.toFixed(1)}%
                </span>
              </button>
            );
          })}
        </div>
      ) : null}
    </DataCard>
  );
}
